// The rows a counterparty's sheets are made of, and the pure steps between the API's sheet and
// the form's draft. The credit assessment (TX-US-0027) and the KYC review (TX-US-0035) are both
// sections of rows; a row's `kind` says what it holds:
//
//   grade    one pick from `options`, and a remark
//   choices  several picks from `options`, and a remark
//   check    a document: received, verified, and a comment
//   pick     one pick from `options`
//   limit    an amount in a currency, and a remark
//   tenor    a number of days, and a remark
//   review   a name and a date
//   date     a date
//   text     one line          longtext  several

export const options = (...pairs) => pairs.map(([code, label]) => ({ code, label }));

export const DEFAULT_CURRENCY = 'USD';

// What a document's Received and Verified are answered with.
export const ANSWERS = options(['YES', 'Yes'], ['NO', 'No'], ['NOT_APPLICABLE', 'N/A']);

const text = (value) => value ?? '';
const trimmed = (value) => value.trim() || null;

// The API's row as the form holds it: every input a string, so none is ever uncontrolled.
const draftRow = (row, value) => {
  switch (row.kind) {
    case 'grade':
      return { grade: text(value?.grade), remark: text(value?.remark) };
    case 'choices':
      return { choices: value?.choices ?? [], remark: text(value?.remark) };
    case 'check':
      return {
        received: text(value?.received),
        verified: text(value?.verified),
        comment: text(value?.comment),
      };
    case 'limit':
      return {
        amount: text(value?.amount?.amount),
        currency: value?.amount?.currency ?? DEFAULT_CURRENCY,
        remark: text(value?.remark),
      };
    case 'tenor':
      return { days: value?.days == null ? '' : String(value.days), remark: text(value?.remark) };
    case 'review':
      return { name: text(value?.name), reviewed_on: text(value?.reviewed_on) };
    default:
      return text(value);
  }
};

// "250,000.50" as the API takes it. A string end to end: an amount never becomes a float.
export const cleanAmount = (typed) => typed.replace(/[\s,]/g, '');

const bodyRow = (row, value) => {
  switch (row.kind) {
    case 'grade':
      return { grade: value.grade || null, remark: trimmed(value.remark) };
    case 'choices':
      return { choices: value.choices, remark: trimmed(value.remark) };
    case 'check':
      return {
        received: value.received || null,
        verified: value.verified || null,
        comment: trimmed(value.comment),
      };
    case 'limit': {
      const amount = cleanAmount(value.amount);
      return {
        amount: amount ? { amount, currency: value.currency.trim().toUpperCase() } : null,
        remark: trimmed(value.remark),
      };
    }
    case 'tenor':
      return { days: value.days.trim() ? Number(value.days) : null, remark: trimmed(value.remark) };
    case 'review':
      return { name: trimmed(value.name), reviewed_on: value.reviewed_on || null };
    case 'pick':
    case 'date':
      return value || null;
    default:
      return trimmed(value);
  }
};

// What the form can refuse in a row before the API does, or null. The API refuses the same.
const rowError = (row, value) => {
  if (row.kind === 'limit' && cleanAmount(value.amount)) {
    if (!/^\d+(\.\d+)?$/.test(cleanAmount(value.amount))) {
      return 'Enter the amount as a number, for example 250,000.';
    }
    if (!/^[A-Za-z]{3}$/.test(value.currency.trim())) {
      return 'Enter a three-letter currency code, for example USD.';
    }
  }
  if (row.kind === 'tenor' && value.days.trim() && !/^\d{1,4}$/.test(value.days.trim())) {
    return 'Enter the tenor as a whole number of days.';
  }
  return null;
};

// "250000.5" as "250,000.5", without passing through a float.
export function formatAmount(amount) {
  const [whole, fraction] = String(amount).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction ? `${grouped}.${fraction}` : grouped;
}

const labelIn = (list, code) => list.find((option) => option.code === code)?.label ?? code;

const withRemark = (value, remark) => [value, remark].filter(Boolean).join(' — ');

// A row of the API's sheet as one line of text, or null when nothing was answered.
export function displayRow(row, value) {
  if (value == null) return null;
  switch (row.kind) {
    case 'grade':
      return withRemark(value.grade && labelIn(row.options, value.grade), value.remark) || null;
    case 'choices':
      return withRemark(
        value.choices.map((code) => labelIn(row.options, code)).join(', '),
        value.remark,
      ) || null;
    case 'check':
      return withRemark(
        [
          value.received && `Received: ${labelIn(ANSWERS, value.received)}`,
          value.verified && `Verified: ${labelIn(ANSWERS, value.verified)}`,
        ].filter(Boolean).join(' · '),
        value.comment,
      ) || null;
    case 'pick':
      return labelIn(row.options, value);
    case 'limit':
      return withRemark(
        value.amount && `${value.amount.currency} ${formatAmount(value.amount.amount)}`,
        value.remark,
      ) || null;
    case 'tenor':
      return withRemark(
        value.days != null && `${value.days} ${value.days === 1 ? 'day' : 'days'}`,
        value.remark,
      ) || null;
    case 'review':
      return [value.name, value.reviewed_on].filter(Boolean).join(', ') || null;
    default:
      return value || null;
  }
}

// A sheet, from its sections: its rows, and the steps between the API's sheet and the draft.
export function sheetOf(sections) {
  const rows = sections.flatMap((section) => section.rows);
  return {
    rows,
    rowLabel: (name) => rows.find((row) => row.name === name)?.label ?? name,
    draftFrom: (sheet = {}) =>
      Object.fromEntries(rows.map((row) => [row.name, draftRow(row, sheet[row.name])])),
    bodyFrom: (draft) =>
      Object.fromEntries(rows.map((row) => [row.name, bodyRow(row, draft[row.name])])),
    // By row name; empty when the form refuses nothing.
    draftErrors: (draft) => Object.fromEntries(
      rows
        .map((row) => [row.name, rowError(row, draft[row.name])])
        .filter(([, error]) => error),
    ),
  };
}
